import { type LeadDetail, LOST_REASONS, OPEN_STAGES, type LeadStage, type LostReason, LEAD_SOURCES, PROCEDURE_AREAS } from '@dnx/contracts';
import { Alert, Button, Field, Select } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { AREA_LABELS, LOST_LABELS, SOURCE_LABELS, STAGE_LABELS } from '../lib/labels.js';
import { assignableQuery } from './queries.js';

const ALL_STAGES = [...OPEN_STAGES, 'won', 'lost'] as const;

/** Etapa + motivo de perda (obrigatório ao perder). Usado no painel do lead e no inbox. */
export function StageControl({ lead, canWrite, compact }: { lead: LeadDetail; canWrite: boolean; compact?: boolean }) {
  const qc = useQueryClient();
  const [pendingLost, setPendingLost] = useState(false);
  const [reason, setReason] = useState<LostReason>('price');
  const move = useMutation({
    mutationFn: (b: { stage: LeadStage; lostReason?: LostReason }) =>
      api<LeadDetail>(`/crm/leads/${lead.id}/stage`, { method: 'POST', body: { ...b, version: lead.version } }),
    onSuccess: () => { setPendingLost(false); void qc.invalidateQueries({ queryKey: ['crm'] }); },
    onError: () => void qc.invalidateQueries({ queryKey: ['crm'] }),
  });
  return (
    <div className="space-y-2">
      <Field label="Etapa do funil">
        <Select value={pendingLost ? 'lost' : lead.stage} disabled={!canWrite || move.isPending}
          onChange={(e) => {
            const v = e.target.value as LeadStage;
            if (v === 'lost') setPendingLost(true);
            else { setPendingLost(false); move.mutate({ stage: v }); }
          }}>
          {ALL_STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
        </Select>
      </Field>
      {pendingLost && (
        <div className="rounded-md border border-border bg-surface-muted p-3">
          <Field label="Motivo da perda" hint="Ajuda a entender onde o funil está vazando.">
            <Select value={reason} onChange={(e) => setReason(e.target.value as LostReason)}>
              {LOST_REASONS.map((r) => <option key={r} value={r}>{LOST_LABELS[r]}</option>)}
            </Select>
          </Field>
          <div className={`mt-2 flex gap-2 ${compact ? '' : 'justify-end'}`}>
            <Button size="sm" variant="ghost" onClick={() => setPendingLost(false)}>Cancelar</Button>
            <Button size="sm" variant="danger" loading={move.isPending} onClick={() => move.mutate({ stage: 'lost', lostReason: reason })}>Marcar como perdido</Button>
          </div>
        </div>
      )}
      {move.error && <Alert tone="warning">{move.error.message}</Alert>}
    </div>
  );
}

export function LeadAttributes({ lead, canWrite }: { lead: LeadDetail; canWrite: boolean }) {
  const qc = useQueryClient();
  const { data: people } = useQuery(assignableQuery);
  const update = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api<LeadDetail>(`/crm/leads/${lead.id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['crm'] }),
  });
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Field label="Interesse">
        <Select value={lead.procedureArea ?? ''} disabled={!canWrite} onChange={(e) => update.mutate({ procedureArea: e.target.value || null })}>
          <option value="">Não informado</option>
          {PROCEDURE_AREAS.map((a) => <option key={a} value={a}>{AREA_LABELS[a]}</option>)}
        </Select>
      </Field>
      <Field label="Origem">
        <Select value={lead.source} disabled={!canWrite} onChange={(e) => update.mutate({ source: e.target.value })}>
          {LEAD_SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
        </Select>
      </Field>
      <Field label="Responsável">
        <Select value={lead.assignedTo?.id ?? ''} disabled={!canWrite} onChange={(e) => update.mutate({ assignedUserId: e.target.value || null })}>
          <option value="">Ninguém</option>
          {people?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </Field>
      {update.error && <Alert tone="danger" className="sm:col-span-3">{update.error.message}</Alert>}
    </div>
  );
}
